import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  ERR_TYPE_NUM_LARGE,
  ERR_TYPE_NUM_SMALL,
  ERR_TYPE_REQUIRED,
  ERR_TYPE_STR_LONG,
  ERR_TYPE_STR_SHORT,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_TXTAREA,
} from '/business/shared/components/questions/helper/fieldEnums';

import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';

export default function useEvChargingSchema() {
  const { t } = useTranslation();

  const electricityKwh = useMemo(() => {
    return {
      propName: 'electricityKwh',
      element: 'electricity_kwh',
      type: FIELD_TYPE_NUMBER,
      defaultValue: 0,
      styleInputWithAppendWidth: '23rem',
      inputAppendComponent: (
        <div>
          {/* @i18n: kWh */}
          <Trans i18nKey="business:emissionFactors.electricity.kwh" />
        </div>
      ),
      label: (
        <>
          <Trans i18nKey="business:QuestionPage.forms.evCharging.electricityKwh.title" />
          &nbsp;
          <ReportingPeriod $green />
          ?*
        </>
      ),
      // labelI18nKey: 'business:QuestionPage.forms.evCharging.electricityKwh.title',
      placeholderI18nKey: null,
      descriptionI18nKey: 'business:QuestionPage.forms.evCharging.electricityKwh.description',

      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNONUMBEROFUNITS',
          errStr: t('business:Errors.evCharging.electricityKwhMissing'),
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          value: 9999999,
          errCode: 'ERRNUMBEROFUNITSTOOLONG',
          errStr: t('business:Errors.evCharging.electricityKwhToHigh'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          value: 0,
          errCode: 'ERRNUMBEROFUNITSTOOLOW',
          errStr: t('business:Errors.evCharging.electricityKwhToLow'),
        },
      ],
    };
  }, [t]);

  const electricityKwhRenewable = useMemo(() => {
    return {
      propName: 'electricityKwhRenewable',
      element: 'electricity_kwh_renewable',
      type: FIELD_TYPE_NUMBER,
      defaultValue: 0,
      styleInputWithAppendWidth: '23rem',
      inputAppendComponent: (
        <div>
          {/* @i18n: kWh */}
          <Trans i18nKey="business:emissionFactors.electricity.kwh" />
        </div>
      ),
      labelI18nKey: 'business:QuestionPage.forms.evCharging.electricityKwhRenewable.title',
      placeholderI18nKey: null,
      description: <></>,

      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRNONUMBEROFUNITS',
          errStr: t('business:Errors.evCharging.electricityKwhRenewableMissing'),
        },
        {
          rule: ERR_TYPE_NUM_LARGE,
          value: 9999999,
          errCode: 'ERRNUMBEROFUNITSTOOLONG',
          errStr: t('business:Errors.evCharging.electricityKwhRenewableToHigh'),
        },
        {
          rule: ERR_TYPE_NUM_SMALL,
          value: 0,
          errCode: 'ERRNUMBEROFUNITSTOOLOW',
          errStr: t('business:Errors.evCharging.electricityKwhRenewableToLow'),
        },
      ],
    };
  }, [t]);

  const howCollected = useMemo(() => {
    return {
      propName: 'howCollected',
      element: 'how_collected',
      type: FIELD_TYPE_TXTAREA,
      labelI18nKey: 'business:QuestionPage.forms.evCharging.howCollected.title',
      description: <Trans i18nKey="business:QuestionPage.forms.evCharging.howCollected.description" />,
      placeholderI18nKey: 'business:QuestionPage.forms.evCharging.howCollected.placeholder',
      validation: [
        {
          rule: ERR_TYPE_REQUIRED,
          errCode: 'ERRDATASOURCE',
          errStr: t('business:Errors.dataSource.provide'),
        },
        {
          rule: ERR_TYPE_STR_LONG,
          value: 3000,
          errCode: 'ERRDATASOURCETOOLONG',
          errStr: t('business:Errors.dataSource.tooLong'),
        },
        {
          rule: ERR_TYPE_STR_SHORT,
          value: 5,
          errCode: 'ERRDATASOURCETOOSHORT',
          errStr: t('business:Errors.dataSource.tooShort'),
        },
      ],
    };
  }, [t]);
  const pageSchema = useMemo(
    () => [electricityKwh, electricityKwhRenewable, howCollected],
    [electricityKwh, electricityKwhRenewable, howCollected],
  );
  return {
    pageSchema,
    isSingularQuestion: true,
  };
}
